import { useSnackbar } from 'notistack';
import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';
import * as api from '../../api/app/plannedQtyDeviationApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { AssetDashboardSetting } from '../../types/app/asset.types';
import {
  PlannedQtyDeviation,
  PlannedQtyDeviationFilterInputs,
} from '../../types/app/plannedQtyDeviation.types';
import { TableContainer, Paper, Button, Tooltip } from '@mui/material';
import { DataGridTable } from '../../components/core/DataGridTable/DataGridTable';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { AlertDialog } from '../../components/core/Dialogs/AlertDialog';
import { useDeleteDialog } from '../../hooks/useDeleteDialog';
import { PlannedQtyDeviationCreate } from './PlannedQtyDeviationCreate';
import { GridValueFormatterParams } from '@mui/x-data-grid';
import moment, { Moment } from 'moment';
import { Timezone } from '../../components/app/TimezoneChanger/TimezoneChanger';
import { dateTimeShowFormat, hoursMinutesShowFormat } from '../../types/core/date.types';
import { utcFormat } from '../../types/core/date.types';
import { useTranslation } from '../../contexts/translation';
import { CustomDialog } from '../../components/core/Dialogs/CustomDialog';
interface Props {
  settingData: AssetDashboardSetting;
  dateFrom: Moment | null;
  timezone: Timezone;
  isReadOnly?: boolean;
}

export function PlannedQtyDeviationList({ settingData, dateFrom, timezone, isReadOnly }: Props) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { id } = useParams();
  const [open, setOpen] = useState(false);
  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => setOpen(false);

  const {
    isOpen: isDeleteDialogOpen,
    idToDelete,
    setDeleteAttempt,
    cancelDeleteAttempt,
    confirmDeleteAttempt,
  } = useDeleteDialog();

  const columns = [
    {
      headerName: t('page.dashboard.planned-qty-deviation.table-col-1'),
      field: 'time',
      width: 200,
      valueFormatter: (params: GridValueFormatterParams) => {
        if (params.value) {
          const addSecond = params.value.includes('59:59');
          return (
            moment
              .utc(params.value.toString())
              .tz(timezone.value)
              .add(-1, 'hour')
              .add(addSecond ? 1 : 0, 'second')
              .format(dateTimeShowFormat) +
            ' - ' +
            moment.utc(params.value.toString()).tz(timezone.value).format(hoursMinutesShowFormat)
          );
        }
      },
    },
    {
      headerName: t('page.dashboard.planned-qty-deviation.table-col-2'),
      field: 'comment',
      width: 250,
    },
    { headerName: t('page.dashboard.planned-qty-deviation.table-col-3'), field: 'qty' },
    {
      field: '  ',
      sortable: false,
      disableColumnMenu: true,
      width: 150,
      renderCell: (params: any) => {
        return (
          <Tooltip title={t('general.action-buttons.delete')}>
            <Button onClick={() => setDeleteAttempt(params.id)}>
              <DeleteIcon />
            </Button>
          </Tooltip>
        );
      },
    },
  ];

  const { enqueueSnackbar } = useSnackbar();
  let requestData = {
    from: dateFrom
      ? moment(dateFrom).startOf('day').utc().format(utcFormat)
      : moment().startOf('year').utc().format(utcFormat),
    to: dateFrom
      ? moment(dateFrom).startOf('day').add(1, 'day').utc().format(utcFormat)
      : moment().endOf('year').utc().format(utcFormat),
    parentId: Number(id),
  } as PlannedQtyDeviationFilterInputs;

  const { data, isLoading, isError, refetch } = useQuery<PlannedQtyDeviation[]>(
    ['plannedQtyDeviation'],
    () => api.getPlannedQtyDeviationListFiltered(requestData),
  );

  useEffect(() => {
    refetch();
  }, [dateFrom, refetch]);

  const { mutate: mutateDeletePlannedQtyDeviation } = useMutation(api.deletePlannedQtyDeviation);

  const handleConfirm = () => {
    if (!idToDelete) return;

    mutateDeletePlannedQtyDeviation(idToDelete, {
      onSuccess: () => {
        enqueueSnackbar(t('page.dashboard.planned-qty-deviation.item-deleted'), {
          variant: 'success',
        });
        queryClient.invalidateQueries(['plannedQtyDeviation']);
        confirmDeleteAttempt();
      },
      onError: (result: any) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data) return <Loader />;
  return (
    <>
      <TableContainer component={Paper}>
        <DataGridTable data={data} columns={columns} pageSize={100} height={1000} />
        {!isReadOnly && (
          <Tooltip title={t('general.action-buttons.new')}>
            <Button onClick={handleOpen}>
              <AddIcon />
            </Button>
          </Tooltip>
        )}
      </TableContainer>
      <CustomDialog
        title={t('page.dashboard.planned-qty-deviation.modal-headline')}
        maxWidth="xs"
        open={open}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <PlannedQtyDeviationCreate
          onClose={handleClose}
          settingData={settingData}
          dateFrom={dateFrom}
          timezone={timezone}
        />
      </CustomDialog>
      <AlertDialog
        open={isDeleteDialogOpen}
        title={t('general.action-buttons.delete')}
        content={t('general.messages.warning.delete-message')}
        handleClose={cancelDeleteAttempt}
        handleConfirm={handleConfirm}
      />
    </>
  );
}
